/**
 * QMF 4.0 bootstrap — contract-driven Quiz Editor.
 * Production splash = Quizzman I-Ching loading standard (app-first).
 * Developer Boot Timeline = opt-in via ?debug=1 | ?qmf-boot=1 | localStorage qmf-dev-boot=1
 * @see /opt/QMF/docs/v4/AI_RULES.md
 * @see ../qmf.schema.json
 */
import { bootstrapFromContractUrl } from '@qmf/runtime';
import { createQuizEditorHttp } from '../components/http.js';
import { scheduleIdle } from './idle.js';
import { initTheme } from './theme.js';

const QMF_SCHEMAS = 'https://ui.quizzman.com/qmf/schemas/';
const APP_V = '2026.09.03.1';
const SW_CACHE = 'qze-static-v21';
const LAZY_MODULES = ['create', 'editor'];

const DEV_BOOT = Boolean(window.__qeDevBoot);
const WARM_BOOT = Boolean(window.__qeWarmBoot);

/** User-facing copy — never expose framework internals in production */
const APP_PHASES = [
  'Starting…',
  'Preparing interface…',
  'Loading project…',
  'Restoring previous session…',
  'Ready',
];

/** Developer-only framework checklist */
const DEV_PHASES = [
  'Initializing Runtime…',
  'Loading Theme Engine…',
  'Connecting Auth…',
  'Preparing Event Bus…',
  'Starting Micro Frontends…',
  'Mounting Layout System…',
  'Initializing Components…',
  'Ready',
];

const PHASES = DEV_BOOT ? DEV_PHASES : APP_PHASES;
const STAGE_COUNT = PHASES.length;

/**
 * Production pacing — same splash UX, shorter waits than the old ~180–220ms / stage.
 * Dev boot keeps a readable timeline.
 */
const PROD_DWELL_MS = 60;
const DEV_DWELL_MS = { 0: 280, 1: 240, 2: 200, 3: 200, 4: 180, 6: 260 };

const statusEl = () => document.getElementById('qm-boot-status');
const fillEl = () => document.getElementById('qm-boot-fill');
const barEl = () =>
  document.querySelector('#qm-initial-loading .qm-load__bar, #qm-initial-loading .qm-boot__progress');
const timelineItems = () =>
  document.querySelectorAll('#qm-boot-timeline .qm-boot__tl-item');

function setBootStage(index, label) {
  const idx = Math.max(0, Math.min(STAGE_COUNT - 1, index));
  const text = label || PHASES[idx];

  const msg = statusEl();
  if (msg) {
    msg.textContent = text;
    msg.classList.remove('qm-boot__status--fade', 'is-fade');
    void msg.offsetWidth;
    msg.classList.add(DEV_BOOT ? 'qm-boot__status--fade' : 'is-fade');
    if (idx >= STAGE_COUNT - 1) msg.classList.add('qm-boot__status--ready');
  }

  if (DEV_BOOT) {
    timelineItems().forEach((el) => {
      const stage = Number(el.getAttribute('data-stage'));
      el.classList.toggle('is-done', stage < idx || idx >= STAGE_COUNT - 1);
      el.classList.toggle('is-active', stage === idx && idx < STAGE_COUNT - 1);
    });
  }

  const pct = Math.round(((idx + 1) / STAGE_COUNT) * 100);
  const fill = fillEl();
  if (fill) fill.style.width = `${pct}%`;
  const bar = barEl();
  if (bar) bar.setAttribute('aria-valuenow', String(pct));

  const shell = document.getElementById('qm-initial-loading');
  if (shell) shell.setAttribute('aria-label', text);
}

function revealReadyTime() {
  const timeEl = document.getElementById('qm-boot-time');
  if (!timeEl) return;
  const ms = Date.now() - (window.__qmLoadStart || Date.now());
  timeEl.textContent = `Ready in ${ms} ms`;
  timeEl.dataset.ready = '1';
  timeEl.classList.add('is-visible');
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function finishBoot() {
  if (DEV_BOOT) revealReadyTime();
  setBootStage(STAGE_COUNT - 1, 'Ready');
  try {
    sessionStorage.setItem('qe-booted', '1');
  } catch {
    /* ignore */
  }

  if (typeof window.hideQuizzmanBootLoader === 'function') {
    await window.hideQuizzmanBootLoader({
      startedAt: window.__qmLoadStart,
      minDisplayMs: DEV_BOOT ? 1400 : WARM_BOOT ? 0 : 100,
      fadeOutMs: WARM_BOOT ? 80 : 120,
      readyText: 'Ready',
    });
    return;
  }

  const el = document.getElementById('qm-initial-loading');
  if (!el) {
    document.documentElement.classList.remove('qm-load-active');
    return;
  }
  el.classList.add('qm-boot--out', 'qm-load--out');
  await sleep(WARM_BOOT ? 80 : 120);
  el.remove();
  document.documentElement.classList.remove('qm-load-active');
}

async function purgeStaleServiceWorker() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      if (reg.scope.includes('/quiz-editor')) await reg.update();
    }
    if (typeof caches === 'undefined') return;
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith('qze-static-') && k !== SW_CACHE)
        .map((k) => caches.delete(k)),
    );
  } catch (err) {
    console.warn('[QZE] SW purge', err);
  }
}

async function advanceStage(index, label, dwellMs = PROD_DWELL_MS) {
  setBootStage(index, label);
  if (dwellMs > 0) await sleep(dwellMs);
}

function dwellFor(index) {
  if (!DEV_BOOT) return PROD_DWELL_MS;
  return DEV_DWELL_MS[index] ?? 200;
}

/** Ensure lazy route modules are loaded before AppRouteChanged consumers must run. */
async function ensureRouteModule(loadModule, routeName) {
  if (!LAZY_MODULES.includes(routeName)) return;
  if (typeof loadModule !== 'function') return;
  await loadModule(routeName);
}

/** After dashboard paint — warm create/editor JS without blocking splash. */
function prefetchLazyModules(loadModule, currentName) {
  if (typeof loadModule !== 'function') return;
  const targets = LAZY_MODULES.filter((id) => id !== currentName);
  if (!targets.length) return;
  scheduleIdle(async () => {
    for (const id of targets) {
      try {
        await loadModule(id);
      } catch (err) {
        console.warn('[QZE] prefetch module', id, err);
      }
    }
  }, { timeout: 2000, delay: 120 });
}

async function boot() {
  initTheme();
  await advanceStage(0, DEV_BOOT ? 'Initializing Runtime…' : 'Starting…', dwellFor(0));

  // SW purge in parallel with stage UI — do not block splash on it in production.
  const swTask = purgeStaleServiceWorker();

  await advanceStage(1, DEV_BOOT ? 'Loading Theme Engine…' : 'Preparing interface…', dwellFor(1));
  await new Promise((r) => requestAnimationFrame(r));

  const contractUrl = new URL(`/quiz-editor/qmf.schema.json?v=${APP_V}`, location.origin).href;
  const appBaseUrl = new URL('/quiz-editor/', location.origin).href;

  if (DEV_BOOT) {
    await advanceStage(2, 'Connecting Auth…', dwellFor(2));
    await advanceStage(3, 'Preparing Event Bus…', dwellFor(3));
    await advanceStage(4, 'Starting Micro Frontends…', dwellFor(4));
    setBootStage(5, 'Mounting Layout System…');
  } else {
    setBootStage(2, 'Loading project…');
  }

  const result = await bootstrapFromContractUrl(contractUrl, {
    appBaseUrl,
    metaContractUrl: `${QMF_SCHEMAS}qmf.schema.schema.json`,
    metaManifestUrl: `${QMF_SCHEMAS}module.manifest.schema.json`,
    // Skip create/editor at boot — load on navigate (qmf.schema.json lazy: true).
    skipLazy: true,
    http: createQuizEditorHttp(),
    configs: {
      auth: {
        loginUrl: 'https://id.quizzman.com/login',
        accountUrl: 'https://id.quizzman.com/account',
        logoutUrl: 'https://id.quizzman.com/logout',
        apiEndpoint: '/quiz-editor/api/auth/me',
        clientId: 'app.quizzman.com',
        mountSelector: '#qe-main',
      },
      shell: {
        headerSelector: '#qmf-lab-header',
        pageTitle: 'Quiz Editor',
        brandName: 'Quizzman',
        qmfVersion: '3.7.2',
      },
      workspace: { mountSelector: '#qe-main' },
      create: { mountSelector: '#qe-main' },
      editor: { mountSelector: '#qe-main' },
    },
  });

  if (typeof window !== 'undefined') {
    window.QMF = window.QMF || {};
    window.QMF.inspect = result.api?.inspect ?? result.inspect;
    window.QMF.loadModule = result.loadModule;
    window.__QZE__ = result;
  }

  // Wire lazy create/editor: load on navigate before route consumers run.
  // Workspace (dashboard) must paint first — do not await create/editor on `/`.
  let currentRouteName = 'workspace';
  try {
    const router = result.container?.resolve?.('svc:shell.router');
    currentRouteName = router?.current()?.name || 'workspace';
    if (router && typeof router.setModuleLoader === 'function') {
      router.setModuleLoader((name) => ensureRouteModule(result.loadModule, name));
      await ensureRouteModule(result.loadModule, currentRouteName);
      if (typeof router.republish === 'function') router.republish();
    }
  } catch (err) {
    console.warn('[QZE] lazy route wire', err);
  }

  if (DEV_BOOT) {
    await advanceStage(6, 'Initializing Components…', dwellFor(6));
  } else {
    setBootStage(3, 'Restoring previous session…');
  }

  await swTask.catch(() => {});
  await finishBoot();
  prefetchLazyModules(result.loadModule, currentRouteName);

  console.info(
    '[QZE] QMF ready',
    result.plugins.map((p) => `${p.name}:${p.__qmfStatus}`).join(', '),
  );
}

boot().catch(async (err) => {
  console.error('[QZE] bootstrap failed', err);
  setBootStage(STAGE_COUNT - 1, DEV_BOOT ? `Boot failed: ${err.message}` : 'Something went wrong');
  const main = document.getElementById('qe-main');
  await finishBoot().catch(() => {});
  if (main) {
    main.innerHTML = `<div class="qmf-card qe-empty">Không khởi tạo được ứng dụng: ${err.message}</div>`;
  }
});
